import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { Maximize2, Minimize2 } from "lucide-react";
import { useOverlayFocus } from "@/hooks/ui/useOverlayFocus";

// ─── Live browser: idle empty-state + real-time VNC feed ───────────────────────

export function LiveBrowserIdle({ canRun }: { canRun: boolean }) {
	return (
		<div className="relative aspect-video w-full overflow-hidden rounded-md border border-border bg-gradient-to-br from-black via-slate-900 to-black">
			{/* scanning line sweeping top→bottom */}
			<div
				data-tst-decorative-motion
				className="absolute inset-x-0 h-px bg-gradient-to-r from-transparent via-primary/70 to-transparent"
				style={{ animation: "tlScan 2.6s linear infinite" }}
			/>
			<div className="flex h-full w-full flex-col items-center justify-center gap-3 text-center">
				<div className="relative">
					<div
						className="absolute inset-0 rounded-full bg-primary/20 blur-xl"
						style={{ animation: "tlGlow 2s ease-in-out infinite" }}
					/>
					<svg
						viewBox="0 0 48 48"
						className="relative h-12 w-12 text-primary/90"
						fill="none"
						stroke="currentColor"
						strokeWidth={2}
					>
						<rect x="6" y="9" width="36" height="30" rx="3" />
						<path d="M6 17h36" />
						<circle cx="11" cy="13" r="1" fill="currentColor" />
						<circle cx="15" cy="13" r="1" fill="currentColor" />
						<circle cx="19" cy="13" r="1" fill="currentColor" />
					</svg>
				</div>
				<div className="space-y-1">
					<div className="text-[13px] font-medium text-white/90">
						Browser is waiting for your test
					</div>
					<div className="text-[11px] text-white/50">
						{canRun
							? "Press Run to launch a live browser session ▸"
							: "Approve the plan to run it — use Approve & Run ▸"}
					</div>
				</div>
				<div className="flex gap-1">
					{[0, 1, 2].map((i) => (
						<span
							key={i}
							data-tst-decorative-motion
							className="h-1.5 w-1.5 rounded-full bg-primary/70"
							style={{
								animation: `tlBounce 1.2s ease-in-out ${i * 0.15}s infinite`,
							}}
						/>
					))}
				</div>
			</div>
			<style>{`
        @keyframes tlScan { 0%{top:0%;opacity:0} 8%{opacity:1} 92%{opacity:1} 100%{top:100%;opacity:0} }
        @keyframes tlGlow { 0%,100%{opacity:.3;transform:scale(.9)} 50%{opacity:.7;transform:scale(1.15)} }
        @keyframes tlBounce { 0%,100%{transform:translateY(0);opacity:.4} 50%{transform:translateY(-4px);opacity:1} }
      `}</style>
		</div>
	);
}

// ─── Live stage wrapper (inline ↔ expanded overlay) ────────────────────────────

export function LiveStage({
	expanded,
	setExpanded,
	showExpand,
	children,
}: {
	expanded: boolean;
	setExpanded: (v: boolean) => void;
	showExpand: boolean;
	children: React.ReactNode;
}) {
	if (expanded) {
		return (
			<ExpandedLiveStage onMinimize={() => setExpanded(false)}>{children}</ExpandedLiveStage>
		);
	}
	return (
		<div className="relative space-y-2">
			{showExpand && (
				<button
					type="button"
					onClick={() => setExpanded(true)}
					className="tst-interactive absolute right-1 top-1 z-10 inline-flex min-h-[24px] items-center gap-1 rounded border border-border bg-card/80 px-1.5 text-[11px] text-muted-foreground hover:bg-card hover:text-foreground"
					title="Expand the live view"
					aria-label="Expand the live view"
				>
					<Maximize2 className="h-3 w-3" /> expand
				</button>
			)}
			{children}
		</div>
	);
}

/**
 * The expanded live view.
 *
 * Portalled to `document.body` on purpose. `position: fixed` is resolved against the nearest
 * ancestor that establishes a containing block, and the app sidebar uses CSS transforms — so a
 * `fixed inset-*` overlay rendered in place was measured against a transformed ancestor instead of
 * the viewport. In practice it sat beside the sidebar, clipped short of the bottom, and let the
 * activity feed underneath show through. (Weave's focus mode portals for the same reason.)
 *
 * The `display: contents` wrapper generates no box, so it changes nothing visually, but it keeps the
 * theme classes in the cascade — without it the `--tst-*` / `--background` custom properties would
 * fall back to the `:root` defaults and the overlay would be themed differently to the app.
 */
function ExpandedLiveStage({
	children,
	onMinimize,
}: {
	children: React.ReactNode;
	onMinimize: () => void;
}) {
	const panelRef = useOverlayFocus(true);

	// Escape minimises, matching every other overlay in the module.
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onMinimize();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [onMinimize]);

	// Don't let the page behind scroll while the overlay owns the screen.
	useEffect(() => {
		const previous = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = previous;
		};
	}, []);

	return createPortal(
		<div className="studio-shell rs-theme tst-route" style={{ display: "contents" }}>
			<div
				ref={panelRef}
				role="dialog"
				aria-modal="true"
				aria-label="Live browser"
				className="fixed inset-0 z-[60] flex flex-col bg-background"
			>
				<div className="tst-header flex shrink-0 items-center gap-2 px-3 py-2">
					<span className="tst-micro shrink-0">Live browser</span>
					<span className="min-w-0 flex-1 truncate text-[12px] text-muted-foreground">
						Watching the run in real time
					</span>
					<button
						type="button"
						onClick={onMinimize}
						className="tst-interactive inline-flex min-h-[24px] shrink-0 items-center gap-1 rounded-md border border-border bg-card px-2 text-[11px] font-medium text-muted-foreground hover:text-foreground"
						title="Minimize the live view (Esc)"
						aria-label="Minimize the live view"
					>
						<Minimize2 className="h-3.5 w-3.5" /> Minimize
					</button>
				</div>
				{/* min-h-0 so the feed inside scrolls instead of pushing the header off screen. */}
				<div className="flex min-h-0 flex-1 flex-col gap-2 overflow-auto p-3">{children}</div>
			</div>
		</div>,
		document.body,
	);
}
